'use client';

/**
 * Props for an inline script whose only job is to run before first paint,
 * from the server-rendered HTML.
 *
 * React 19.2 errors in development ("Encountered a script tag while rendering
 * React component") whenever it CREATES an executable <script> on the client,
 * because a client-created script never runs. That happens whenever the
 * subtree mounts on the client instead of hydrating: a client navigation into
 * a layout, or the root remounting. The script's work was already done by the
 * server HTML on the first load, and the components that need it re-apply
 * their state in effects, so on the client the element is dead weight.
 *
 * So the server copy stays an ordinary script (the browser runs it while
 * parsing the HTML), and the client copy declares itself a data block, which
 * is what it really is there. Hydration keeps the server element as it is
 * (suppressHydrationWarning covers the differing type), and a client-created
 * copy is inert by type instead of inert by accident.
 *
 * This is the same outcome as the upstream next-themes fix that has not been
 * released (pacocoursey/next-themes#386, issues #385 and #397), done through
 * the library's public `scriptProps` option.
 */
export const prePaintScriptProps: { type?: string } =
  typeof window === 'undefined' ? {} : { type: 'application/json' };

/** An inline pre-paint script, rendered so the client never creates a live copy. */
export function PrePaintScript({ code }: { code: string }) {
  return (
    <script
      {...prePaintScriptProps}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: code }}
    />
  );
}
